// ============================================================================
// fade.js
// Fade cue handling with correct lifecycle, transport reset, and ownership
// ============================================================================

/*
Fade semantics:
  - fade(in)     → hidden until triggered
  - fade(inout)  → hidden until triggered
  - fade(out)    → visible until triggered
  - explicit from: always wins

Fade OWNS opacity.
Nothing else should permanently set opacity on fade targets.
*/

// -----------------------------------------------------------------------------
// Internal registry (one fade per element)
// -----------------------------------------------------------------------------
import { publish, bindParam, isSignalRef, isOscRef } from '../control/paramBinding.js';
import { registerAnimation } from './animation.js';   // runtime-only use; the import cycle is the same one trans.js has
import { sendOSC } from '../system/oscillaOSCClient.js';
import { worldLeftX } from '../system/worldCoords.js';
import { playheadIsPast } from '../system/scoreTime.js';
import { animeEase } from '../control/easing.js';
import { secs, secPerBeat } from './beats.js';
import { fadePhaseOf, blinkPhase } from './animMath.js';   // the phase sync carries
import { onReset } from "../system/teardown.js";
import { transport } from "../system/transportState.js";

window._fadeCues ??= new Map();
onReset("project", function forgetFades() { window._fadeCues.clear(); });

// -----------------------------------------------------------------------------
// Resolve fade target (shared logic)
// -----------------------------------------------------------------------------
// Modes that turn up in target: — some authored scores carry
// fade(..., target:inout) and friends. Read those as the mode they mean
// instead of hunting for an element with that id.
const FADE_MODES = new Set([
  "in", "out", "inout", "pulse", "pulseslow", "pulsefast", "strobe", "blink", "stop",
]);

// Params with the legacy target:<mode> confusion resolved.
function fadeParams(ast) {
  const params = Object.fromEntries((ast.args || []).map(a => [a.type, a.value]));
  if (params.target && !params.mode &&
      FADE_MODES.has(String(params.target).toLowerCase())) {
    params.mode = params.target;
    delete params.target;
  }
  return params;
}

function resolveFadeTarget(ast, cueElement) {
  const params = fadeParams(ast);
  const targetId = params.target || "self";

  let target = null;

  if (targetId === "self") {
    if (cueElement instanceof Element) {
      target = cueElement;
    } else if (typeof cueElement === "string") {
      target = document.getElementById(cueElement);
    }
  } else {
    target = document.getElementById(targetId);
  }

  // Last resort for element-less triggers (remote cue ids, next: chains, timer
  // callbacks) where "self" has no element to resolve against: the uid param
  // doubles as the host element id for cues authored by the DSL editor.
  // Only for implicit self — an explicit target: that misses is a typo, and
  // fading some other element instead would just hide the mistake.
  if (!(target instanceof Element) && targetId === "self" && params.uid) {
    target = document.getElementById(params.uid);
  }

  // NEVER fall back to the score root or <body>: an unresolvable target would
  // fade the whole score to a blank white page.
  if (!(target instanceof Element)) {
    console.warn(`[cueFade] Target "${targetId}" not resolvable — skipping (no whole-score fallback).`);
    return null;
  }

  return target;
}

// -----------------------------------------------------------------------------
// Prime fade target (load + transport reset)
// -----------------------------------------------------------------------------
export function primeFadeTargetFromAST(ast, cueElement) {
  if (!ast || !Array.isArray(ast.args)) return;

  const params = fadeParams(ast);
  const mode = (params.mode || "in").toLowerCase();

  const from = Number(
    params.from ??
    (mode === "out" ? 1 : 0) // in + inout start hidden
  );

  const target = resolveFadeTarget(ast, cueElement);
  if (!target) return;

  // Fade owns opacity → assert authority
  target.style.setProperty("opacity", from, "important");
}

// -----------------------------------------------------------------------------
// Forget a fade target (call when its DSL is edited away / the cue is removed)
//
// Without this the element stays in _fadeCues forever: resetAllFadePriming()
// keeps re-hiding it on every rewind, and the primed inline opacity keeps it
// invisible for the rest of the session — so a removed fade() looks permanent.
// -----------------------------------------------------------------------------
export function forgetFadeTarget(element) {
  if (!element) return;
  // Only unwind fades this element actually registered — otherwise
  // unregistering any cue would strip an inline opacity something else owns.
  const ast = window._fadeCues?.get(element);
  if (!ast) return;
  window._fadeCues.delete(element);

  // …and its entry in the animation registry, or the sync leader keeps
  // broadcasting a fade that no longer exists.
  const uid = element.dataset?.animUid;
  if (uid && window.oscillaAnimRegistry?.[uid]?.kind === "fade") {
    delete window.oscillaAnimRegistry[uid];
  }

  // Clean up whatever the cue primed, which is the resolved target — not
  // necessarily this element (fade(target:other) primes `other`).
  const target = resolveFadeTarget(ast, element) || element;
  anime.utils.remove(target);
  if (target._blinkTimer) {
    clearInterval(target._blinkTimer);
    target._blinkTimer = null;
  }
  // Release the opacity authority fade asserted while priming
  target.style?.removeProperty?.("opacity");
}

// -----------------------------------------------------------------------------
// Where a fade cue sits on the timeline, in world units — the space
// window.playheadX is measured in. assignCues already captured it, before any
// animation moved anything; measure it only if the record is missing.
// -----------------------------------------------------------------------------
function cueWorldX(element) {
  const rec = window.cues?.find?.((c) => c.element === element);
  if (Number.isFinite(rec?.homeWorldX)) return rec.homeWorldX;
  try {
    const x = worldLeftX(element);
    return Number.isFinite(x) ? x : null;
  } catch { return null; }
}

// -----------------------------------------------------------------------------
// Reset fade priming (call on rewind / jump / restart)
//
// Re-priming means putting the target back to its `from` — hidden, for the
// in/inout modes. Not every fade on every nudge of the transport: a re-hidden
// fade whose cue is well behind the playhead would never come back, because
// the playhead does not cross that cue again from there.
//
// A fade behind the playhead has happened, and keeps its end state until the
// playhead is rewound past the cue — then it is armed again. `all` is for a
// full transport reset, where everything returns to its initial state
// whatever the start offset works out to be.
// -----------------------------------------------------------------------------
export function resetAllFadePriming({ all = false } = {}) {
  if (!window._fadeCues || window._fadeCues.size === 0) return;

  const playheadX = transport.playheadX;

  for (const [element, ast] of window._fadeCues.entries()) {
    if (!all && playheadIsPast(cueWorldX(element), playheadX, !!transport.scrollReversed)) continue;
    primeFadeTargetFromAST(ast, element);
  }
}

// -----------------------------------------------------------------------------
// Handle fade cue trigger
// -----------------------------------------------------------------------------
export function handleFadeCueFromAST(ast, cueElement) {
  const params = fadeParams(ast);

  const mode  = (params.mode || "in").toLowerCase();

  /**
   * A fade's numbers, with follow() allowed.
   *
   * SAMPLED, not followed. A fade is a one-shot ramp whose tween is built from
   * from/to/dur at the moment it starts — there is nothing for a later value to
   * change. So the signal is read when the cue fires and that reading is used,
   * which is the same contract dur: already has on rotate(), scale() and
   * color(). "Fade to wherever the fader is now" is the useful gesture; "fade
   * to a moving target" is not a fade.
   */
  const sampled = (name, fallback, lo, hi) => {
    const v = params[name];
    if (!isSignalRef(v) && !isOscRef(v)) return Number(v ?? fallback);
    const b = bindParam(v, () => {}, { min: lo, max: hi });
    b?.unbind?.();                       // one reading, then let go
    return Number.isFinite(b?.value) ? b.value : Number(fallback);
  };

  // dur:, hold:, delay: and time: are beats; a followed dur: is read in
  // beats (its range's) and then turned. The 1 s default is not a beat.
  const k     = secPerBeat();
  const dur   = params.dur == null ? 1 : secs(sampled("dur", 1, 0, 60), k);
  const hold  = Number(secs(params.hold ?? 0, k));
  const delay = Number(secs(params.delay ?? 0, k));
  const ease  = params.ease || "linear";

  const from = sampled("from", mode === "out" ? 1 : 0, 0, 1);
  const to   = sampled("to",   mode === "out" ? 0 : 1, 0, 1);

  const target = resolveFadeTarget(ast, cueElement);

  if (!target) {
    console.warn("[cueFade] No valid target, aborting.");
    return;
  }

  // ---------------------------------------------------------------------------
  // UID (resolve BEFORE OSC)
  // ---------------------------------------------------------------------------
  let uid =
    params.uid ||
    target.id ||
    (typeof cueElement === "string" ? cueElement : null) ||
    "fade";

  uid = String(uid).replace(/[^a-zA-Z0-9_\-]/g, "");

  // ---------------------------------------------------------------------------
  // OSC sender
  // ---------------------------------------------------------------------------
  // osc:1 opts in (same convention as rotate/scale/trans); oscaddr: overrides
  // the default /oscilla/fade/<uid>. Goes through the shared client so the
  // OSC monitor, mute and per-address throttle all apply.
  function sendFadeOSC(value) {
    if (!(Number(params.osc) || 0)) return;
    const addr = params.oscaddr ?? params.oscAddr ?? null;
    const msg = { type: "osc_fade", uid, value, timestamp: Date.now() };
    if (addr) msg.addr = String(addr).trim();
    sendOSC(msg);
  }

  // ---------------------------------------------------------------------------
  // Network sync registration (see animSync.js): in the registry, the leader
  // broadcasts it and followers correct it. The run below is wrapped in
  // start() so a late joiner or remote resume can re-run it from a given position (_dragResumeT).
  // ---------------------------------------------------------------------------
  const syncArg = params.sync;
  const cfg = {
    kind: "fade", uid, astArgs: ast.args || [],
    trig: String(params.trig || "playhead").toLowerCase(),
    mode, dur, from, to, hold, delay,
    sync: syncArg === undefined ? undefined
        : (syncArg !== false && syncArg !== "false" && syncArg !== 0 && syncArg !== "0"),
    _anim: null,
  };
  const regEl = (cueElement instanceof Element) ? cueElement : target;
  cfg._start = start;
  registerAnimation(regEl, "fade", cfg, start);
  start();
  return;

  function start() {
  cfg._armState = "running";
  // ---------------------------------------------------------------------------
  // Prepare target for animation
  // ---------------------------------------------------------------------------
  anime.utils.remove(target);

  // Release fade priming authority so Anime.js can control opacity
  target.style.removeProperty("opacity");
  target.style.opacity = from;

  const common = {
    ease: animeEase(ease),
    duration: dur * 1000,
    delay: delay * 1000,
    onUpdate: anim => {
      const o = parseFloat(target.style.opacity);
      const v = Number.isFinite(o) ? o : from;
      cfg._currentT = fadePhaseOf(anim);
      cfg._phase = cfg._currentT;
      cfg._alternateDir = anim.backwards ? -1 : 1;
      sendFadeOSC(v);
      publish("fade", uid, { opacity: v });
    },
    onComplete: () => {
      sendFadeOSC(to);
      publish("fade", uid, { opacity: to });
    },
  };

  // ---------------------------------------------------------------------------
  // Modes
  // ---------------------------------------------------------------------------
  switch (mode) {

    case "in":
    case "out":
      attach(anime.animate(target, { ...common, opacity: [from, to] }));
      break;

    case "inout":
      attach(anime.animate(target, {
        ...common,
        opacity: [from, to],
        alternate: true,
        loop: 1            // one extra pass: there and back
      }));
      break;

    case "pulse":
      attach(anime.animate(target, {
        ...common,
        opacity: [from, to],
        alternate: true,
        loop: true,
        loopDelay: hold * 1000
      }));
      break;

    case "pulseslow":
      attach(anime.animate(target, {
        ...common,
        opacity: [from, to],
        alternate: true,
        loop: true,
        duration: dur * 2000,
        loopDelay: hold * 1000
      }));
      break;

    case "pulsefast":
      attach(anime.animate(target, {
        ...common,
        opacity: [from, to],
        alternate: true,
        loop: true,
        duration: dur * 500,
        loopDelay: hold * 1000
      }));
      break;

    case "strobe":
      attach(anime.animate(target, {
        ...common,
        opacity: [from, to],
        alternate: true,
        loop: true,
        duration: dur * 100,
        ease: anime.steps(2),
        loopDelay: hold * 1000
      }));
      break;

    case "blink": {
      const period = dur * 1000;
      const half = period / 2;
      clearInterval(target._blinkTimer);
      target._oscillaFadeAnim = null;

      // Start aligned to a resume position if one was handed to us.
      let startedAt = performance.now() - (cfg._dragResumeT ?? 0) * period;
      delete cfg._dragResumeT;

      const applyBlink = () => {
        const t = blinkPhase(performance.now(), startedAt, period);
        const v = t < 0.5 ? from : to;
        cfg._currentT = t; cfg._phase = t; cfg._alternateDir = 1;
        target.style.opacity = v;
        sendFadeOSC(v);
        publish("fade", uid, { opacity: v });
      };
      const arm = () => {
        clearInterval(target._blinkTimer);
        // first tick lands on the next half-period boundary, then every half
        const t = blinkPhase(performance.now(), startedAt, period);
        const toBoundary = (t < 0.5 ? 0.5 - t : 1 - t) * period;
        target._blinkTimer = setTimeout(() => {
          applyBlink();
          target._blinkTimer = setInterval(applyBlink, half);
        }, toBoundary);
      };
      applyBlink();
      arm();

      cfg._anim = {
        pause() { clearInterval(target._blinkTimer); clearTimeout(target._blinkTimer); },
        play()  { arm(); },
        setPhase(t) { startedAt = performance.now() - Math.max(0, Math.min(1, t)) * period; applyBlink(); arm(); },
        setDirection() {},
        getPhase() { return cfg._currentT ?? 0; },
      };
      break;
    }

    case "stop":
      anime.utils.remove(target);
      clearInterval(target._blinkTimer);
      target.style.opacity = from;
      cfg._armState = "stopped";
      cfg._anim = null;
      target._oscillaFadeAnim = null;
      break;

    default:
      console.warn("[cueFade] Unknown mode:", mode);
  }

  // ---------------------------------------------------------------------------
  // Optional auto-stop
  // ---------------------------------------------------------------------------
  // kept on the target, so a run again replaces it: the first run's timer
  // would stop the second run early
  const timeLimit = Number(secs(params.time || 0, k));
  clearTimeout(target._oscillaFadeStopTimer);
  target._oscillaFadeStopTimer = null;
  if (timeLimit > 0) {
    target._oscillaFadeStopTimer = setTimeout(() => {
      target._oscillaFadeStopTimer = null;
      anime.utils.remove(target);
      clearInterval(target._blinkTimer);
      target.style.opacity = from;
      cfg._armState = "stopped";
      cfg._anim = null;
      target._oscillaFadeAnim = null;
      sendFadeOSC(from);
    }, timeLimit * 1000);
  }

  // Keep the instance and expose the sync/observer interface. A resume
  // position (late joiner, remote resume) seeks straight to it.
  function attach(inst) {
    target._oscillaFadeAnim = inst;
    if (cfg._dragResumeT != null) {
      inst.iterationProgress = Math.max(0, Math.min(1, cfg._dragResumeT));
      delete cfg._dragResumeT;
    }
    cfg._anim = {
      pause() { inst.pause(); },
      play()  { inst.play(); },
      setPhase(t) {
        const p = Math.max(0, Math.min(1, Number(t) || 0));
        inst.iterationProgress = p;
        cfg._currentT = p; cfg._phase = p;
      },
      setDirection(d) {
        const want = d >= 0 ? 1 : -1;
        if ((inst.backwards ? -1 : 1) !== want) inst.reverse();
        cfg._alternateDir = want;
      },
      getPhase() { return cfg._currentT ?? 0; },
    };
  }
  }   // end start()
}
